import React, { useCallback } from 'react';
import { Flex, Text, Heading, Box, Switch, TextInput, Icon, Link } from '@televet/kibble-ui';
import { useSurveySettingsContext, ISurveyGeneralSettings } from 'pages/Survey/context/SurveySettingsContext';
import { url } from 'shared/utils/validation';
import { SurveyType } from 'shared/types/graphql';

interface LinkToReviewProps {
  handleChange: (field: keyof ISurveyGeneralSettings, newValue: boolean | string) => void;
}

enum ReviewLinkToggleType {
  Neutral = 'Neutral',
  Satisfied = 'Satisfied',
}

const LinkToReview = ({ handleChange }: LinkToReviewProps): JSX.Element => {
  const {
    generalSettings: { showLinkToLeaveReviewNeutral, showLinkToLeaveReviewSatisfied, linkToLeaveReview },
    surveyType,
  } = useSurveySettingsContext();

  const handleShowLinktoReviewToggle = useCallback(
    (toggle: ReviewLinkToggleType) => {
      const key =
        toggle === ReviewLinkToggleType.Neutral ? 'showLinkToLeaveReviewNeutral' : 'showLinkToLeaveReviewSatisfied';
      const newValue =
        toggle === ReviewLinkToggleType.Neutral ? !showLinkToLeaveReviewNeutral : !showLinkToLeaveReviewSatisfied;

      handleChange(key, newValue);
    },
    [handleChange, showLinkToLeaveReviewNeutral, showLinkToLeaveReviewSatisfied],
  );

  const handleLinkToLeaveReviewInputChange = useCallback(
    (e) => {
      if (e.target.value !== linkToLeaveReview) {
        handleChange('linkToLeaveReview', e.target.value);
      }
    },
    [handleChange, linkToLeaveReview],
  );

  return (
    <Box className="settings__container" mt={6}>
      <Flex direction="column">
        <Flex direction="column">
          <Heading className="settings__header" size="md" mb={2}>
            Link to Leave a Review
          </Heading>
          <Text className="settings__description" variant="subtle" mb={4}>
            This link will display to
            {surveyType === SurveyType.Nps ? ' passives and promoters ' : ' satisfied customers '}
            at the end of the survey if enabled
          </Text>
        </Flex>
        <Flex mt={1}>
          <TextInput
            value={linkToLeaveReview || ''}
            onChange={handleLinkToLeaveReviewInputChange}
            leftElement={<Icon size="sm" variant="subtle" name="chainLink" />}
            placeholder="https://www.yelp.com/review..."
            isInvalid={linkToLeaveReview ? !url.test(linkToLeaveReview) : false}
            errorText="You must provide a valid URL starting with http:// or https://"
          />
        </Flex>
        <Flex>
          <Link size="sm" mt="2" href="https://support.google.com/business/answer/3474122" isExternal>
            How to find your Google review link
          </Link>
        </Flex>
        <Flex my={5} align="baseline" justify="space-between">
          <Heading className="settings__header" size="md">
            Reviewer Type
          </Heading>
          <Heading className="settings__header" size="md">
            Enable
          </Heading>
        </Flex>
        <Flex className="settings__fieldRow-input" align="baseline" justify="space-between" mb={4}>
          <Text>{surveyType === SurveyType.Csat ? 'Good (4) & Excellent (5)' : 'Promoters (9-10)'}</Text>
          <Flex alignItems="baseline">
            <Switch
              isChecked={!!showLinkToLeaveReviewSatisfied}
              onChange={(): void => handleShowLinktoReviewToggle(ReviewLinkToggleType.Satisfied)}
              pr={2}
            />
            <Flex>
              <Text size="xs">{!!showLinkToLeaveReviewSatisfied ? 'ON' : 'OFF'}</Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex className="settings__fieldRow-input" align="baseline" justify="space-between" mb={4}>
          <Text>{surveyType === SurveyType.Csat ? 'Satisfactory (3)' : 'Passives (7-8)'}</Text>
          <Flex alignItems="baseline">
            <Switch
              isChecked={!!showLinkToLeaveReviewNeutral}
              onChange={(): void => handleShowLinktoReviewToggle(ReviewLinkToggleType.Neutral)}
              pr={2}
            />
            <Flex alignItems="center">
              <Text size="xs">{!!showLinkToLeaveReviewNeutral ? 'ON' : 'OFF'}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default LinkToReview;
